// Packaging House claims (migration 0117, Umer 2026-10-06) — the rules, pure, so they are tested
// without a database or a render.
//
// A packager TAKES part of a house grant onto their own table ("On my table"); what is open to take
// on a grant is its remaining grams minus the active claims on it. A batch is made FROM a claim and
// never refused for its numbers: used − packed is kept as the run's variance (admins see it,
// packagers do not). A packager sees only their own claims and batches; the WM tier (warehouse
// manager, manager, admin, super admin) sees everything and may approve any batch; the maker may
// approve their own.
import { ProductionRun, ReturnKind, ReturnStatus, RunStatus, UserRole, type LotAssignment, type LotReturn, type PackingClaim, type Product, type ProductionOutput, type RunWarning } from '../types';
import { productUnit } from './productUnits';
import { isSuperTier } from './roles';

export const CLAIM_EXPIRY_DAYS = 7;
/** Over / under beyond this share of the material used is highlighted to admins. */
export const OVER_UNDER_FLAG_PCT = 10;
const DUST_G = 0.001;

export const sbClaimFromRow = (r: any): PackingClaim => {
  const c: PackingClaim = {
    id: String(r.id),
    houseId: String(r.house_id),
    assignmentId: String(r.assignment_id),
    lotId: String(r.lot_id),
    lotName: r.lot_name ?? '',
    workerId: String(r.worker_id),
    workerName: r.worker_name ?? '',
    gramsClaimed: Number(r.grams_claimed) || 0,
    gramsRemaining: Number(r.grams_remaining) || 0,
    status: (r.status as PackingClaim['status']) || 'active',
    claimedAt: r.claimed_at ? Date.parse(r.claimed_at) : 0,
    lastActivityAt: r.last_activity_at ? Date.parse(r.last_activity_at) : 0,
  };
  if (r.count_unit != null) c.countUnit = r.count_unit;
  if (r.released_reason != null) c.releasedReason = r.released_reason;
  if (r.released_at) c.releasedAt = Date.parse(r.released_at);
  return c;
};

/** The WM tier sees every claim and batch of a house and may approve any batch. */
export const seesEveryBatch = (role: UserRole | string | undefined | null): boolean =>
  role === UserRole.WAREHOUSE_MANAGER || role === UserRole.MANAGER || role === UserRole.ADMIN || isSuperTier(role);

export const isActiveClaim = (c: PackingClaim): boolean => c.status === 'active' && c.gramsRemaining > DUST_G;

/** One grant of the house with what is still open to take on it (grant remaining − active claims). */
export interface OpenMaterial {
  assignmentId: string;
  lotId: string;
  lotName: string;
  openGrams: number;
  countUnit?: string;
  notes?: string;
  targetProductIds: string[];
  assignedAt: number;
}

/** What is open to take, grant by grant — the database's packing_grant_open (0122), mirrored: per lot in
 *  this house, the grants' remaining minus the active claims minus the material given back to the
 *  Warehouse and not refused (Give Material Back writes a return against the lot and never reduces the
 *  grant — the shared pile subtracts the returns the same way, AppContext#computeHoldings). The returns
 *  are charged to the oldest grants first, each up to what it has free. */
export function openToTake(
  assignments: readonly LotAssignment[], claims: readonly PackingClaim[], houseId: string, returns: readonly LotReturn[] = [],
): OpenMaterial[] {
  const held = new Map<string, number>();
  for (const c of claims) if (c.status === 'active') held.set(c.assignmentId, (held.get(c.assignmentId) || 0) + c.gramsRemaining);
  const givenBack = new Map<string, number>();
  for (const r of returns) {
    if (r.houseId !== houseId || r.kind !== ReturnKind.BULK || (r.status ?? ReturnStatus.CONFIRMED) === ReturnStatus.REJECTED) continue;
    givenBack.set(r.lotId, (givenBack.get(r.lotId) || 0) + r.grams);
  }
  const charged = new Map<string, number>();
  const openById = new Map<string, number>();
  const group = assignments.filter((a) => a.houseId === houseId)
    .sort((x, y) => (x.assignedAt - y.assignedAt) || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
  for (const a of group) {
    const free = Math.max(0, a.remainingGrams - (held.get(a.id) || 0));
    const charge = Math.min(free, Math.max(0, (givenBack.get(a.lotId) || 0) - (charged.get(a.lotId) || 0)));
    charged.set(a.lotId, (charged.get(a.lotId) || 0) + charge);
    openById.set(a.id, free - charge);
  }
  return assignments
    .filter((a) => a.houseId === houseId && a.status === 'active')
    .map((a) => ({
      assignmentId: a.id, lotId: a.lotId, lotName: a.lotName,
      openGrams: openById.get(a.id) ?? 0,
      countUnit: a.countUnit, notes: a.notes, targetProductIds: a.targetProductIds ?? [], assignedAt: a.assignedAt,
    }))
    .filter((m) => m.openGrams > DUST_G)
    .sort((x, y) => x.assignedAt - y.assignedAt);
}

/** This person's table in this house: their active claims, oldest first. */
export const myTable = (claims: readonly PackingClaim[], userId: string, houseId: string): PackingClaim[] =>
  claims.filter((c) => c.workerId === userId && c.houseId === houseId && isActiveClaim(c)).sort((a, b) => a.claimedAt - b.claimedAt);

/** Everyone's tables in this house (the admin's "who has what"). */
export const houseTables = (claims: readonly PackingClaim[], houseId: string): PackingClaim[] =>
  claims.filter((c) => c.houseId === houseId && isActiveClaim(c)).sort((a, b) => a.workerName.localeCompare(b.workerName) || a.claimedAt - b.claimedAt);

/** The batches this viewer sees: their own, or every one of the house for the WM tier. */
export const visibleRuns = (runs: readonly ProductionRun[], userId: string, role: UserRole | string | undefined, houseId: string): ProductionRun[] =>
  runs.filter((r) => r.houseId === houseId && (seesEveryBatch(role) || r.workerId === userId));

/** The maker approves their own batch; the WM tier any. */
export const canApproveRun = (run: Pick<ProductionRun, 'workerId'>, userId: string, role: UserRole | string | undefined): boolean =>
  seesEveryBatch(role) || run.workerId === userId;

/** The maker edits until approval (status waiting or sent back); the WM tier too. */
export const canEditRun = (run: Pick<ProductionRun, 'workerId' | 'status'>, userId: string, role: UserRole | string | undefined): boolean =>
  (run.status === RunStatus.PENDING_PACKAGER || run.status === RunStatus.REJECTED) && (seesEveryBatch(role) || run.workerId === userId);

/** When an untouched claim goes back to the pile on its own. */
export const claimExpiresAt = (c: Pick<PackingClaim, 'lastActivityAt'>, days: number = CLAIM_EXPIRY_DAYS): number =>
  c.lastActivityAt + days * 86_400_000;

/** The weight of one pack from the product's set size: grams for a weight product, the item count on
 *  a counted lot, 0 when the product has no size (or a size the lot's unit cannot be compared with). */
export function unitGramsOf(p: Pick<Product, 'unitAmount' | 'unitKind'> | undefined, countUnit?: string): number {
  const u = p ? productUnit(p) : null;
  if (!u) return 0;
  if (countUnit) return u.kind === 'each' ? u.amount : 0;
  if (u.kind === 'g') return u.amount;
  if (u.kind === 'mg') return u.amount / 1000;
  return 0;
}

/** A product can go into a batch only once it has a size (0117: nothing is typed by hand). */
export const productHasSize = (p: Pick<Product, 'unitAmount' | 'unitKind'> | undefined): boolean => !!p && productUnit(p) !== null;

/** Used − packed, with the share of the material used; null when a pack's weight is unknown. */
export function overUnder(run: Pick<ProductionRun, 'totalGramsConsumed' | 'outputs' | 'variance'>): { grams: number; pct: number; flagged: boolean } | null {
  if (!run.totalGramsConsumed) return null;
  if (run.outputs.some((o) => !(o.unitGrams > 0))) return null;
  const grams = run.variance;
  const pct = (Math.abs(grams) / run.totalGramsConsumed) * 100;
  return { grams, pct, flagged: pct > OVER_UNDER_FLAG_PCT };
}

/** The warnings a batch must accept before it is sent: a product the hand-off did not name. */
export function buildRunWarnings(outputs: readonly ProductionOutput[], targetProductIds: readonly string[], actor: { id: string; name: string }, now: number = Date.now()): RunWarning[] {
  if (!targetProductIds.length) return [];
  const out: RunWarning[] = [];
  for (const o of outputs) {
    if (targetProductIds.includes(o.productId)) continue;
    out.push({ code: 'off_target', text: `${o.productName} is not one of the products named in the instructions.`, acceptedBy: actor.id, acceptedByName: actor.name, acceptedAt: now });
  }
  return out;
}

/** "Sent for approval", "Sent back", "In stock" — the batch's state in plain words. */
export const runStateLabel = (status: RunStatus): string =>
  status === RunStatus.PENDING_PACKAGER ? 'Waiting in Confirm & Stock'
    : status === RunStatus.REJECTED ? 'Sent back — please fix'
      : status === RunStatus.CONFIRMED ? 'In the Storage House'
        : 'Packed';

/** The database refuses an amount in grams ("Only 224.0 of Pink Kush is open to take", "Only 224.0 is on
 *  the table" — 0117); say it in the unit the screen uses ("0.5 lb", or the lot's count). Any other
 *  message is returned unchanged (Umer's staging walk, 2026-10-07). */
export const claimRefusalText = (message: string, fmtAmount: (grams: number) => string): string => {
  const m = /^Only (-?\d+(?:\.\d+)?) (of .+ is open to take|is on the table)$/.exec(message.trim());
  return m ? `Only ${fmtAmount(Number(m[1]))} ${m[2]}` : message;
};

/** How long a returned / used-up / expired claim stays loaded: long enough to Edit a batch made from it
 *  until it is approved (the dialog reads the batch's claim), short enough that the page does not
 *  download every claim ever made (Umer, 2026-10-07). */
export const CLAIMS_KEEP_DAYS = 30;

/** The rows the page follows: every active claim, plus the ones released since `cutoffIso`. Query and
 *  accept rule (never the channel), so a claim that is used up or expires arrives and stays until it
 *  ages out. */
export function claimsLiveFilter(cutoffIso: string) {
  const cut = Date.parse(cutoffIso);
  return {
    key: `claims:${CLAIMS_KEEP_DAYS}d`,
    or: `status.eq.active,released_at.gte.${cutoffIso}`,
    accept: (r: any) => r?.status === 'active' || (r?.released_at != null && Date.parse(r.released_at) >= cut),
    columns: ['status', 'released_at'],
  };
}

/** "Adjust the lot to what I packed" (0123): packed − used, in grams (or counts) — + the lot goes up, − it goes
 *  down. null when a product has no weight to compare (pack weight 0), where the database refuses the box. */
export function lotAdjustmentOf(outputs: readonly ProductionOutput[], usedGrams: number): number | null {
  if (!outputs.length || outputs.some((o) => !(o.unitGrams > 0))) return null;
  return outputs.reduce((s, o) => s + o.qty * o.unitGrams, 0) - usedGrams;
}
